/**
 * The licence board's shared vocabulary: formatters, tones, and the labels
 * that explain why a SKU was left out of the headline.
 *
 * All threshold logic lives here rather than in the panels, so the KPI row,
 * the bars, and the tables can never disagree about what "healthy" means. The
 * seat arithmetic itself is the backend's (`nova.licenses.service`) — this
 * module only formats what it is given.
 */

/** The tone union shared by `Statistic`, `Badge`, and `Meter`. */
export type LicenceTone = "accent" | "success" | "warning" | "danger" | "neutral";

/** Utilisation at or above this is healthy — the seats are being used. */
const UTILISATION_HEALTHY_PCT = 90;
/** Below this, enough seats are idle to be worth a conversation. */
const UTILISATION_POOR_PCT = 60;

/** Why a SKU is excluded from the headline, in words a reader can act on. */
export const EXCLUSION_LABEL: Record<string, string> = {
  unlimited: "Free / unlimited",
  "company-scoped": "Tenant capacity",
  suspended: "Suspended",
  "not-in-paid-allowlist": "Outside paid allowlist",
};

/** Human labels for the reasons an account is a reclaim candidate. */
export const RECLAIM_REASON_LABEL: Record<string, string> = {
  disabled: "Disabled account",
  guest: "Guest",
  "inactive-90d": "Inactive 90d+",
};

export function formatSeats(seats: number | null | undefined): string {
  if (seats == null) {
    return "—";
  }
  return seats.toLocaleString();
}

export function formatPct(pct: number | null | undefined): string {
  if (pct == null) {
    return "—";
  }
  return `${pct.toFixed(1)}%`;
}

/** Format a money figure. Returns null when no rate is configured for it. */
export function formatMoney(value: number | null | undefined, currency: string): string | null {
  if (value == null) {
    return null;
  }
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(value);
  } catch {
    // An unrecognised currency code must not blank out the number.
    return `${Math.round(value).toLocaleString()} ${currency}`;
  }
}

/** Higher utilisation is better, so the scale runs the opposite way to uptime —
 *  but only up to 100%. Past that the tenant has handed out seats it does not
 *  own, which is a failure, not an A grade. */
export function utilisationTone(pct: number | null | undefined): LicenceTone {
  if (pct == null) {
    return "neutral";
  }
  if (pct > 100) {
    return "danger";
  }
  if (pct >= UTILISATION_HEALTHY_PCT) {
    return "success";
  }
  if (pct >= UTILISATION_POOR_PCT) {
    return "warning";
  }
  return "danger";
}

/** Tone for a count where zero is the good outcome (unused seats, idle accounts). */
export function wasteTone(count: number): LicenceTone {
  if (count === 0) {
    return "success";
  }
  return count > 0 ? "warning" : "neutral";
}

/** Segment colours for the seat composition bars.
 *
 *  Status tones, not categorical ones: assigned is working, unused is idle
 *  inventory, and an over-assignment is broken — somebody loses access when the
 *  grace period closes. Never used colour-alone; every segment is labelled. */
export const SEAT_SEGMENT_COLOR = {
  assigned: "var(--norican-accent)",
  unused: "var(--norican-warning)",
  overAssigned: "var(--norican-danger)",
} as const;

/** What the board calls each side of the seat model, in one place.
 *
 *  "Purchased" and "expiring" are the two numbers this board exists to keep
 *  apart, so the words for them are shared rather than retyped per panel. */
export const SEAT_LABEL = {
  purchased: "Paid seats",
  grace: "Expiring capacity",
  assigned: "Assigned",
  unused: "Unused",
  overAssigned: "At risk when grace ends",
  suspended: "Suspended seats",
  lockedOut: "Locked out",
} as const;

/** What the two cancelled-capacity buckets are, reused across panels.
 *
 *  `prepaidUnits` has four buckets and this board reports four numbers. These
 *  are the two nobody was showing: both are capacity on a **cancelled**
 *  subscription, so neither can be assigned and neither is ever added to paid
 *  seats — the same rule that keeps expiring capacity out of the total. */
export const CANCELLED_CAPACITY_EXPLAINER =
  "Seats on subscriptions that have been cancelled. Suspended seats can still be reactivated before Microsoft deletes them; locked-out seats cannot. Neither can be assigned to anyone, so neither counts as paid capacity.";

/** Caption for the suspended-seats tile — names the locked-out figure too.
 *
 *  The two buckets are deliberately never summed: they mean different things
 *  (reactivatable vs not) and one figure hiding the other is how the seat
 *  maths went wrong the first time. */
export function cancelledSeatsCaption(suspended: number, lockedOut: number): string {
  if (lockedOut > 0) {
    return `${formatSeats(lockedOut)} more locked out, past reactivation`;
  }
  if (suspended > 0) {
    return "Cancelled, still reactivatable";
  }
  return "No cancelled capacity";
}

/** Render a service plan's off-seat count as "N of M", or an em dash.
 *
 *  The em dash is load-bearing: `inactive_users: null` means the seats' plan
 *  state could not be read, which is not the same finding as "0 of 240 seats
 *  have it switched off" and must not render like it. */
export function formatPlanOffSeats(row: {
  inactive_users: number | null;
  measured_users: number;
}): string {
  if (row.inactive_users == null) {
    return "—";
  }
  return `${formatSeats(row.inactive_users)} of ${formatSeats(row.measured_users)}`;
}

/** One sentence explaining what expiring capacity is, reused across panels. */
export const GRACE_EXPLAINER =
  "Seats on subscriptions that have already ended. Microsoft honours them until the grace period closes, so they are not capacity the tenant owns and are never counted as paid seats.";

/** Render a date as a short absolute stamp, for "report covers" style captions. */
export function formatDate(value: string | null | undefined): string {
  if (!value) {
    return "—";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "—";
  }
  return parsed.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
